import { useEffect, useState, type ReactNode } from 'react';

import { useWindowWidth } from '../../hooks/useWindowWidth';

import {
  marketingAnchors,
  marketingCta,
  marketingFooter,
  marketingNav,
} from '../../../../app/_shared/marketing/landingContent';
import { KynwardLogo } from '../brand/KynwardLogo';
import { EmberBackground } from '../ember/EmberBackground';
import { useSmartStoreHref } from '../../hooks/useSmartStoreHref';
import { marketingAppDownloadVisible } from '../../config';
import { markWaitlistSignupSource } from '../../waitlist/waitlistSignupSource';
import { isExternalStoreUrl } from '../../utils/storePlatform';
import { MarketingFooterColumns } from './MarketingFooterColumns';
import '../../MarketingPage.css';
import './landingRef.css';
import './marketingProduct.css';

function scrollToAnchor(id: string) {
  const onHome = window.location.pathname === '/' || window.location.pathname === '';
  if (id === marketingAnchors.waitlist) {
    markWaitlistSignupSource('landing-footer');
  }
  const el = document.getElementById(id);
  if (onHome && el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  window.location.href = `/#${id}`;
}

type Props = {
  children: ReactNode;
};

export function MarketingSiteLayout({ children }: Props) {
  const narrowNav = useWindowWidth() < 900;
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const showAppDownload = marketingAppDownloadVisible();
  const downloadHref = `/#${marketingAnchors.download}`;
  const navDownloadHref = useSmartStoreHref(downloadHref);
  const navOpensStore = isExternalStoreUrl(navDownloadHref);
  const waitlistHref = `/#${marketingAnchors.waitlist}`;

  return (
    <EmberBackground>
      <div className="ml ml-landingPremium">
        <header className={`ml-nav nav${navScrolled ? ' ml-nav--scrolled' : ''}`}>
          <div className="ml-nav__inner nav__inner">
            <a className="ml-nav__wordmark" href="/">
              <KynwardLogo size={28} glow="subtle" className="ml-nav__logo" />
              <span className="ml-nav__wordmarkText">Kynward</span>
            </a>
            <div className={`ml-nav__links ${narrowNav ? 'ml-nav__links--hideMobile' : ''}`}>
              <a className="ml-nav__link" href={`/#${marketingAnchors.how}`}>
                {marketingNav.theFlow}
              </a>
              <a className="ml-nav__link" href={`/#${marketingAnchors.meetups}`}>
                {marketingNav.meetups}
              </a>
              <a className="ml-nav__link" href={`/#${marketingAnchors.safety}`}>
                {marketingNav.safety}
              </a>
              <a className="ml-nav__link" href={`/#${marketingAnchors.faq}`}>
                {marketingNav.questions}
              </a>
            </div>
            {showAppDownload ? (
              <a
                className="ml-nav__cta"
                href={navDownloadHref}
                {...(navOpensStore ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {marketingCta.downloadApp}
              </a>
            ) : (
              <a className="ml-nav__cta" href={waitlistHref}>
                {marketingCta.joinFullReleaseList}
              </a>
            )}
          </div>
        </header>

        {children}

        <footer className="ml-footer ml-footer--columns">
          <div className="ml-footer__inner ml-footer__inner--wide page-wrap">
            <MarketingFooterColumns
              tag={marketingFooter.tag}
              columns={marketingFooter.columns}
              onScrollToAnchor={scrollToAnchor}
              year={new Date().getFullYear()}
            />
          </div>
        </footer>
      </div>
    </EmberBackground>
  );
}
